import React, { useMemo, useState } from 'react'
import { upperCase } from 'lodash'

import styles from './_language-selector.module.scss'
import {
  Alert,
  Button,
  EmptyState,
  FormFooter,
  hasValue,
  Icon,
  SearchBar,
  SelectDisplay,
  SideDrawer,
  useIsMobileView,
} from '../../../../module'
import { c } from '../../../../translations/LibraryTranslationService'
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguageKeys,
} from '../../../TranslationTools/TranslationTools.models'

export type LanguageSelectorProps = {
  /** The language that is currently selected */
  selectedLanguage: SupportedLanguageKeys
  /** The language code for the supported languages */
  supportedLanguages: SupportedLanguageKeys[]
  /** Function to change the language */
  setLanguage: (language: SupportedLanguageKeys) => void
  /** Checks to see if LeftNav is expanded or collapsed */
  expanded: boolean
}

const LanguageSelector = ({
  selectedLanguage,
  supportedLanguages,
  setLanguage,
  expanded,
}: LanguageSelectorProps): React.JSX.Element => {
  const languages = useMemo(
    () =>
      supportedLanguages.map((language) => {
        const languageObj = SUPPORTED_LANGUAGES[language]
        return {
          name: language,
          label: `${upperCase(language)} - ${languageObj.translated_language} ${languageObj.language !== 'English' ? `(${languageObj.language})` : ''}`,
        }
      }),
    [supportedLanguages],
  )

  const [selectedOption, setSelectedOption] = useState(selectedLanguage),
    [isOpen, setIsOpen] = useState(false),
    [search, setSearch] = useState(''),
    [languagesCopy, setLanguagesCopy] = useState(languages),
    isMobile = useIsMobileView()

  const closeDrawer = () => setIsOpen(false)

  const updateLanguages = (value: string) => {
    setSearch(value)
    const filteredLanguages = languages.filter((language) =>
      language.label.toLowerCase().includes(value.toLowerCase()),
    )
    setLanguagesCopy(hasValue(value) ? filteredLanguages : languages)
  }

  const selectedLanguageObj = SUPPORTED_LANGUAGES[selectedLanguage]

  return (
    <>
      <Button as='unstyled' onClick={() => setIsOpen(true)}>
        <div className={styles.container}>
          <div className={styles.flagAndText}>
            <Icon icon='language' color='purple' />
            {expanded ? (
              <div className={styles.languageTextContainer}>
                <div className={`${styles.languageText} animated slideInLeft`}>
                  <span className={styles.languageCode}>
                    {selectedLanguage} -{' '}
                  </span>
                  <span>{selectedLanguageObj.translated_language} </span>
                  {selectedLanguageObj.language !== 'English' && (
                    <span>({selectedLanguageObj.language})</span>
                  )}
                </div>
              </div>
            ) : null}
          </div>
          <Icon icon='carat' iconSize='8px' />
        </div>
      </Button>
      <SideDrawer
        isOpen={isOpen}
        closeCallout={closeDrawer}
        headerContent={c('selectLanguage')}
        {...(isMobile ? { layerPosition: 1 } : {})}
        noContentPadding
        footerContent={({ close }) => (
          <FormFooter
            resetButtonProps={{
              onClick: () => {
                setSelectedOption(selectedLanguage)
                setSearch('')
                setLanguagesCopy(languages)
              },
            }}
            cancelButtonProps={{
              onClick: () => {
                setSelectedOption(selectedLanguage)
                close()
              },
            }}
            saveButtonProps={{
              onClick: () => {
                setLanguage(selectedOption)
                close()
              },
            }}
          />
        )}
      >
        <div className={styles.alertAndSearch}>
          <Alert type='info' text={c('languageSelectorInfoAlert')} />
          <SearchBar
            value={search}
            placeholder={c('searchLanguages')}
            onChange={updateLanguages}
          />
        </div>
        {languagesCopy.length > 0 ? (
          <SelectDisplay
            options={languagesCopy}
            callout={(language) => setSelectedOption(language.name)}
            // @ts-expect-error active prop type definition is incorrect
            active={selectedOption}
          />
        ) : (
          <EmptyState primaryText={c('noResultsFound')} />
        )}
      </SideDrawer>
    </>
  )
}

export default LanguageSelector
